import React, { useEffect, useState, useCallback } from 'react'
import { FlexCenter, H2 } from '../../lib/components'
import { VerifiedEmailPresenter } from './verify-email-presenter'
import { userServices } from '../../services'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { Verification } from '../../lib/types'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import {
  completeEmailVerificationLogin,
  getVerifyEmailError,
  isEmailVerificationLoginData,
} from '../../lib/utils/post-verification-auth'
import { applyPostLoginNavigation } from '../../lib/utils/post-login'

const VerifyEmailContainer = () => {
  const [isVerified, setIsVerified] = useState(false)
  const [isLinkExpired, setLinkExpired] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const verify_v1 = useCallback(
    async (code: string, user: string) => {
      try {
        const response = await userServices.verifyEmail_v1(code, user, Verification.verifyEmail)
        const error = getVerifyEmailError(response.data)
        if (response.status === 400 && error?.code === 'link-expired') {
          setIsVerified(true)
          setLinkExpired(true)
          return
        }

        if (response.status === 200 && isEmailVerificationLoginData(response.data)) {
          captureEvent(EVENTS.email_verified, { user })
          completeEmailVerificationLogin(dispatch, response.data)
          if (response.data.mustSetPassword) {
            // Accounts opened with an emailed temporary password choose their own first.
            navigate('/set-password')
            return
          }
          await applyPostLoginNavigation(
            dispatch,
            navigate,
            response.data.authToken,
            response.data.userId,
            response.data.email,
            response.data.name,
            response.data.acceptedNDA ?? false,
          )
          return
        }

        setIsVerified(true)
      } catch (err: unknown) {
        const errorData =
          typeof err === 'object' && err !== null && 'data' in err
            ? getVerifyEmailError((err as { data: unknown }).data)
            : null
        dispatch(
          showBanner({
            text: errorData?.message || 'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
        setIsVerified(false)
        navigate('/')
      }
    },
    [dispatch, navigate],
  )

  useEffect(() => {
    const code = searchParams.get('code')
    const user = searchParams.get('user')
    if (user && code) verify_v1(code, user)
    else {
      dispatch(
        showBanner({
          text: 'Something went wrong. Please try again.',
          variant: 'error-banner',
        }),
      )
      navigate('/login')
    }
  }, [])

  const handleClick = () => navigate('/login')
  return (
    <FlexCenter classes={['p-24']}>
      {isVerified ? (
        <VerifiedEmailPresenter handleClick={handleClick} isLinkExpired={isLinkExpired} />
      ) : (
        <H2 text='Verifying email, please wait..' />
      )}
    </FlexCenter>
  )
}

export { VerifyEmailContainer }
